import { useState, useSyncExternalStore, type ReactNode } from 'react';
import { Button, Input, Modal, Textarea } from './index';

/**
 * App-styled replacement for window.confirm()/alert() (review Low: "Dialogs").
 * Call `ask()` / `choose()` from any handler; <ConfirmHost/> (rendered by <Toaster/>) shows the dialog.
 */
export type Choice<T extends string> = { value: T; label: string; variant?: 'primary' | 'secondary' | 'danger' };
export type AskOptions = {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button. Default true: most confirmations guard something destructive. */
  danger?: boolean;
  /** Typed confirmation: the confirm button stays disabled until this exact text is entered. */
  typeToConfirm?: string;
};
export type TextInput = { label: string; initial?: string; placeholder?: string; required?: boolean; multiline?: boolean; maxLength?: number };
type Pending = { opts: AskOptions; choices: Choice<string>[]; input?: TextInput; resolve: (v: string | null) => void };

let queue: Pending[] = [];
const listeners = new Set<() => void>();
const emit = () => { queue = [...queue]; listeners.forEach(l => l()); };
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/** Resolves with the chosen value, or null when cancelled / dismissed. */
export function choose<T extends string>(opts: Omit<AskOptions, 'confirmLabel' | 'danger'>, choices: Choice<T>[]): Promise<T | null> {
  return new Promise(resolve => { queue.push({ opts, choices, resolve: resolve as (v: string | null) => void }); emit(); });
}

/** Resolves true when confirmed. */
export async function ask(opts: AskOptions | string): Promise<boolean> {
  const o = typeof opts === 'string' ? fromText(opts) : opts;
  const v = await choose(o, [{ value: 'ok', label: o.confirmLabel ?? 'Confirm', variant: o.danger === false ? 'primary' : 'danger' }]);
  return v === 'ok';
}

const DANGER = /^(delete|remove|archive|cancel|unpublish|stop|leave|close|retire|set a temporary|make a new)/i;
/** "Archive X? It stops…" → title "Archive X?", body "It stops…", button "Archive". */
export function fromText(text: string): AskOptions {
  const i = text.indexOf('?');
  const title = i > 0 ? text.slice(0, i + 1).trim() : text.trim();
  const body = i > 0 ? text.slice(i + 1).trim() || undefined : undefined;
  const verb = title.match(/^(Set a temporary password|Make a new badge|Go back|Stop requiring|[A-Z][a-z]+)/)?.[0] ?? 'Confirm';
  return { title, body, confirmLabel: verb, danger: DANGER.test(title) };
}

/** Replaces prompt(): resolves with the entered text ('' allowed unless required), or null when cancelled. */
export function askText(opts: Omit<AskOptions, 'typeToConfirm'> & { input: TextInput }): Promise<string | null> {
  return new Promise(resolve => {
    queue.push({ opts, input: opts.input, choices: [{ value: 'ok', label: opts.confirmLabel ?? 'OK', variant: opts.danger ? 'danger' : 'primary' }], resolve });
    emit();
  });
}

/** Information only (replaces alert()). */
export async function notice(title: string, body?: ReactNode): Promise<void> {
  await choose({ title, body, cancelLabel: '' }, [{ value: 'ok', label: 'OK', variant: 'primary' }]);
}

/** Test hook: settle every open dialog (jsdom tests without a host). */
export function _settleAll(v: string | null) { const q = queue; queue = []; q.forEach(p => p.resolve(v)); emit(); }

export function ConfirmHost() {
  const q = useSyncExternalStore(subscribe, () => queue);
  const cur = q[0];
  const [typed, setTyped] = useState('');
  if (!cur) return null;
  const done = (v: string | null) => { queue = queue.filter(p => p !== cur); setTyped(''); cur.resolve(v !== null && cur.input ? typed.trim() : v); emit(); };
  const { opts } = cur;
  const blocked = (!!opts.typeToConfirm && typed.trim() !== opts.typeToConfirm) || (!!cur.input?.required && !typed.trim());
  return (
    <Modal open onClose={() => done(null)} title={opts.title}
      footer={<>
        {opts.cancelLabel !== '' && <Button variant="ghost" onClick={() => done(null)} data-testid="confirm-cancel">{opts.cancelLabel ?? 'Cancel'}</Button>}
        {cur.choices.map((c, i) => (
          <Button key={c.value} variant={c.variant ?? 'primary'} disabled={blocked && (c.variant === 'danger' || !!cur.input)} onClick={() => done(c.value)}
            data-testid={cur.choices.length === 1 ? 'confirm-ok' : `confirm-choice-${c.value}`} data-autofocus={i === cur.choices.length - 1 && !opts.typeToConfirm ? true : undefined}>{c.label}</Button>
        ))}
      </>}>
      {opts.body && <div className="whitespace-pre-line text-sm text-slate-600 dark:text-slate-300">{opts.body}</div>}
      {cur.input && (
        <form className="mt-1 block text-sm" onSubmit={e => { e.preventDefault(); if (!blocked) done('ok'); }}>
          <label className="block">
            <span className="text-slate-600 dark:text-slate-300">{cur.input.label}</span>
            {cur.input.multiline
              ? <Textarea className="mt-1" rows={3} value={typed} placeholder={cur.input.placeholder} maxLength={cur.input.maxLength} onChange={e => setTyped(e.target.value)} data-testid="confirm-text" data-autofocus />
              : <Input className="mt-1" value={typed} placeholder={cur.input.placeholder} maxLength={cur.input.maxLength} onChange={e => setTyped(e.target.value)} data-testid="confirm-text" data-autofocus />}
          </label>
        </form>
      )}
      {opts.typeToConfirm && (
        <label className="mt-3 block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Type <b className="font-mono">{opts.typeToConfirm}</b> to confirm</span>
          <Input className="mt-1" value={typed} onChange={e => setTyped(e.target.value)} data-testid="confirm-typed" autoComplete="off" />
        </label>
      )}
    </Modal>
  );
}
